import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const CalendarAdvance = ({ bookings, selectedMonth, onMonthChange, onDaySelect }) => {
    const [displayedMonth, setDisplayedMonth] = useState(selectedMonth);
    const navigate = useNavigate();
    useEffect(() => {
        setDisplayedMonth(selectedMonth);
    }, [selectedMonth]);

    const currentMonth = displayedMonth.getMonth();
    const currentYear = displayedMonth.getFullYear();
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const leadingBlanks = firstDay.getDay();
    const daysArray = Array.from({ length: leadingBlanks + daysInMonth }, (_, index) => {
        const day = index - leadingBlanks + 1;
        //return day > 0 ? day : null;
        if (day > 0) {
            const date = new Date(currentYear, currentMonth, day);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is zero-based, so add 1
            const dayOfMonth = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${dayOfMonth}`; // Formats as "yyyy-MM-dd" in local time (IST)
        }
        return null;
    });

    const handlePreviousMonth = () => {
        const newDate = new Date(currentYear, currentMonth - 1, 1);
        setDisplayedMonth(newDate);
        onMonthChange(newDate);
    };

    const handleNextMonth = () => {
        const newDate = new Date(currentYear, currentMonth + 1, 1);
        setDisplayedMonth(newDate);
        onMonthChange(newDate);
    };

    function getDayPart(dateString) {
        if (!dateString) {
            return "";
        }
        const date = new Date(dateString);
        return date.getDate();
    }

    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    function getTotalRoomCount(day) {
        const dayStartEpoch = getEpoch(day, "00:00");
        const dayEndEpoch = getEpoch(day, "23:59");
        let totalRooms = 0;
        for (const booking of bookings) {
            const { checkInEpoch, checkOutEpoch } = booking;

            if (checkInEpoch < dayEndEpoch && checkOutEpoch > dayStartEpoch) {
                totalRooms += booking.roomBookings.reduce((sum, rb) => sum + rb.numberOfRooms, 0);
            }
        }
        return totalRooms;
    }

    function getRoomsEndTodayCount(day) {
        const dayStartEpoch = getEpoch(day, "00:00");
        const dayEndEpoch = getEpoch(day, "23:59");
        let totalRooms = 0;

        for (const booking of bookings) {
            const { checkInEpoch, checkOutEpoch } = booking;
            if (checkInEpoch < dayStartEpoch && checkOutEpoch >= dayStartEpoch && checkOutEpoch <= dayEndEpoch) {
                totalRooms += booking.roomBookings.reduce((sum, rb) => sum + rb.numberOfRooms, 0);
            }
        }
        return totalRooms;
    }

    function getRoomsStartTodayCount(day) {
        const dayStartEpoch = getEpoch(day, "00:00");
        const dayEndEpoch = getEpoch(day, "23:59");
        let totalRooms = 0;

        for (const booking of bookings) {
            const { checkInEpoch, checkOutEpoch } = booking;
            if (checkInEpoch >= dayStartEpoch && checkInEpoch <= dayEndEpoch && checkOutEpoch > dayEndEpoch) {
                totalRooms += booking.roomBookings.reduce((sum, rb) => sum + rb.numberOfRooms, 0);
            }
        }
        return totalRooms;
    }

    function getRoomsContinuedCount(day) {
        const dayStartEpoch = getEpoch(day, "00:00");
        const dayEndEpoch = getEpoch(day, "23:59");
        let totalRooms = 0;

        for (const booking of bookings) {
            const { checkInEpoch, checkOutEpoch } = booking;
            if (checkInEpoch < dayStartEpoch && checkOutEpoch > dayEndEpoch) {
                totalRooms += booking.roomBookings.reduce((sum, rb) => sum + rb.numberOfRooms, 0);
            }
        }
        return totalRooms;
    }

    function hasAnyBookings(day) {
        const dayStartEpoch = getEpoch(day, "00:00");
        const dayEndEpoch = getEpoch(day, "23:59");
        let totalRooms = 0;
        for (const booking of bookings) {
            const { checkInEpoch, checkOutEpoch } = booking;

            if (checkInEpoch < dayEndEpoch && checkOutEpoch > dayStartEpoch) {
                totalRooms += booking.roomBookings.reduce((sum, rb) => sum + rb.numberOfRooms, 0);
            }
        }
        return totalRooms !== 0;
    }

    function getEpoch(date, time, seconds = 0) {
        const [hours, minutes] = time.split(':').map(Number);
        const combinedDate = new Date(date);
        combinedDate.setHours(hours);
        combinedDate.setMinutes(minutes);
        combinedDate.setSeconds(seconds);
        combinedDate.setMilliseconds(seconds * 100);

        return combinedDate.getTime();
    }

    const handleClick = (day) => {
        navigate(`/manager/bookings/${day}`);
    };

    return (
        <div className="p-5">
            <div className="flex justify-between items-center mb-4">
                <button onClick={handlePreviousMonth} className="px-3 py-1 bg-green-300 rounded hover:bg-green-500">
                    {"<<<"}
                </button>
                <h2 className="text-2xl font-bold text-center">
                    {firstDay.toLocaleString('default', { month: 'long' })} {currentYear}
                </h2>
                <button onClick={handleNextMonth} className="px-3 py-1 bg-green-300 rounded hover:bg-green-500">
                    {">>>"}
                </button>
            </div>

            <div className="grid grid-cols-7 gap-4 mb-2 text-center font-semibold">
                {weekDays.map((week) => (
                    <div key={week} className="text-gray-600">
                        {week}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-3">
                {daysArray.map((day, index) => (
                    <div
                        key={index}
                        className={`border rounded-lg p-3 flex flex-col items-center ${day ? '' : 'bg-gray-100'}`} >
                        {day ? (
                            <>
                                <span className={`text-m font-normal select-none flex items-center justify-center h-7 w-7 rounded-full ${day === todayString ? 'bg-blue-500 text-white' : ''}`}>{getDayPart(day)}</span>

                                <div className="flex flex-col items-center space-x-0 space-y-2 xl:flex-row xl:space-x-2 xl:space-y-0">
                                    {(() => {
                                        if (hasAnyBookings(day)) {
                                            return <div className="cursor-pointer relative group" onClick={(e) => handleClick(day)}>
                                                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 transition-opacity">
                                                    {`Total: ${getTotalRoomCount(day)}`}
                                                </div>
                                                <span className='text-m font-bold flex items-center justify-center h-10 w-10 mb-2 rounded-r-full
                                        bg-red-500 text-white'>{getRoomsEndTodayCount(day)}</span>
                                                <span className='text-m font-bold flex items-center justify-center h-10 w-10 mb-2
                                        bg-yellow-500 text-black'>{getRoomsContinuedCount(day)}</span>
                                                <span className='text-m font-bold flex items-center justify-center h-10 w-10 rounded-l-full
                                        bg-green-500 text-white'>{getRoomsStartTodayCount(day)}</span>
                                            </div>;
                                        } else {
                                            return <span className='text-m font-bold flex items-center justify-center h-10 w-10'>0</span>;
                                        }
                                    })()}
                                </div>
                            </>
                        ) : null}
                    </div>
                ))}
            </div>
        </div >
    );
};

export default CalendarAdvance;